import { usePage, router } from '@inertiajs/react';
import { Head } from '@inertiajs/react';
import type { SettingsPageProps } from '@/types';
import { Navbar } from '@/components';

export default function Settings() {
  const { flash, googleConnection } = usePage<SettingsPageProps>().props;

  function handleDisconnect() {
    if (confirm('Are you sure you want to disconnect your Google account?')) {
      router.delete('/google/oauth/disconnect');
    }
  }

  function handleConnect() {
    // Use full page redirect for OAuth flow (not Inertia)
    window.location.href = '/google/oauth/authorize';
  }

  return (
    <>
      <Head title="Settings" />
      <div className="min-h-screen bg-gray-50">
        <Navbar currentSection="settings" />

        <main className="max-w-2xl mx-auto px-4 py-12">
          {flash.notice && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
              {flash.notice}
            </div>
          )}

          {flash.alert && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {flash.alert}
            </div>
          )}

          <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>

          <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Google Account</h2>
              <p className="text-sm text-gray-500 mt-1">
                Connect your Google account to enable Google Sheets and Gmail integration.
              </p>
            </div>

            <div className="p-6">
              {googleConnection.connected ? (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      Connected
                    </span>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div>
                      <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                        Email
                      </span>
                      <span className="text-gray-900">{googleConnection.email}</span>
                    </div>

                    {googleConnection.scopes && googleConnection.scopes.length > 0 && (
                      <div>
                        <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                          Connected Scopes
                        </span>
                        <ul className="space-y-1">
                          {googleConnection.scopes.map((scope) => (
                            <li key={scope} className="text-sm text-gray-600">
                              {formatScope(scope)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {googleConnection.expiresAt && (
                      <div>
                        <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                          Token Expires
                        </span>
                        <span className="text-sm text-gray-600">
                          {new Date(googleConnection.expiresAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleDisconnect}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                  >
                    Disconnect Google Account
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                      <span className="w-2 h-2 rounded-full bg-gray-400" />
                      Not Connected
                    </span>
                  </div>

                  <p className="text-gray-600 mb-6">
                    Connect your Google account to enable importing transactions from Google Sheets
                    and other Google integrations.
                  </p>

                  <button
                    onClick={handleConnect}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Connect Google Account
                  </button>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

// Helper to format scope URLs into readable names
function formatScope(scope: string): string {
  const scopeMap: Record<string, string> = {
    'https://www.googleapis.com/auth/spreadsheets': 'Google Sheets (read/write)',
    'https://www.googleapis.com/auth/spreadsheets.readonly': 'Google Sheets (read only)',
    'https://www.googleapis.com/auth/drive.file': 'Google Drive (files created by app)',
    'https://www.googleapis.com/auth/drive.readonly': 'Google Drive (read only)',
    'https://www.googleapis.com/auth/gmail.readonly': 'Gmail (read only)',
    'https://www.googleapis.com/auth/gmail.modify': 'Gmail (read/write)',
    'https://www.googleapis.com/auth/gmail.send': 'Gmail (send)',
  };

  return scopeMap[scope] || scope;
}
